
import React from 'react';

interface EventDescriptionProps {
  description: string | null;
}

export default function EventDescription({ description }: EventDescriptionProps) {
  if (!description) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4">About This Event</h2>
        <p className="text-sm text-gray-400 italic">No description provided.</p>
      </div>
    );
  }

  const paragraphs = description.split('\n').filter((p) => p.trim());

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
      <h2 className="text-lg font-bold text-gray-900 mb-5">About This Event</h2>
      <div className="space-y-4">
        {paragraphs.map((paragraph, idx) => {
          const trimmed = paragraph.trim();

          // Detect heading‑like lines (short, no period, could be a section title)
          const isHeading =
            trimmed.length < 80 &&
            !trimmed.endsWith('.') &&
            !trimmed.endsWith('!') &&
            (trimmed.startsWith('What') ||
              trimmed.startsWith('Who') ||
              trimmed.startsWith('How') ||
              trimmed.startsWith('Why') ||
              trimmed.startsWith('When') ||
              trimmed.startsWith('Where') ||
              trimmed.endsWith(':') ||
              trimmed.endsWith('?'));

          // Detect bullet‑like lines
          const isBullet =
            trimmed.startsWith('•') ||
            trimmed.startsWith('-') ||
            trimmed.startsWith('✓') ||
            trimmed.startsWith('✅') ||
            /^\d+[\.\)]/.test(trimmed);

          if (isHeading) {
            return (
              <h3
                key={idx}
                className="text-base font-semibold text-gray-900 mt-4 first:mt-0"
              >
                {trimmed}
              </h3>
            );
          }

          if (isBullet) {
            const bulletText = trimmed
              .replace(/^[•\-✓✅]\s*/, '')
              .replace(/^\d+[\.\)]\s*/, '');
            return (
              <div key={idx} className="flex items-start gap-2.5 pl-1">
                <i className="ri-check-line text-teal-500 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-sm text-gray-600 leading-relaxed">{bulletText}</p>
              </div>
            );
          }

          return (
            <p key={idx} className="text-sm text-gray-600 leading-relaxed">
              {trimmed}
            </p>
          );
        })}
      </div>
    </div>
  );
}
